import { Card, CardContent, TextField } from '@flexui-io/flexui-components';
import { SliderAsyncButton } from '@slider/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ApiProblemError } from '../app/api/slider-fetch';
import { AuthPage } from '../features/auth/auth-appearance';
import { redirectAuthenticatedVisitor } from '../features/auth/session-guard';
import { useRequestPasswordReset } from '../generated/api/auth/auth';

type ForgotPasswordFields = { email: string };

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: ({ context }) => redirectAuthenticatedVisitor({ queryClient: context.queryClient }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const form = useForm<ForgotPasswordFields>({ defaultValues: { email: '' } });
  const [sent, setSent] = useState(false);
  const requestReset = useRequestPasswordReset({
    mutation: { onSuccess: () => setSent(true) },
  });
  const isRateLimited =
    requestReset.error instanceof ApiProblemError && requestReset.error.code === 'RATE_LIMITED';

  return (
    <AuthPage>
      <Link aria-label={t('brand.name')} className="brand-mark auth-brand" to="/">
        <span aria-hidden="true">S</span>
        {t('brand.name')}
      </Link>
      <Card className="auth-card">
        <CardContent>
          <p className="eyebrow">{t('auth.resetEyebrow')}</p>
          <h1>{t('auth.forgotTitle')}</h1>
          <p className="auth-description">{t('auth.forgotDescription')}</p>
          {sent ? (
            <div aria-live="polite" className="auth-success" role="status">
              <CheckCircle2 aria-hidden="true" size={22} />
              <p>{t('auth.resetRequested')}</p>
            </div>
          ) : (
            <form
              onSubmit={(event) =>
                void form.handleSubmit((data) => requestReset.mutate({ data }))(event)
              }
            >
              <TextField
                autoComplete="email"
                dir="ltr"
                closeIcon={false}
                id="reset-email"
                inputSize="md"
                label={t('auth.email')}
                leftIcon={<Mail aria-hidden="true" size={18} />}
                placeholder={t('auth.emailPlaceholder')}
                required
                type="email"
                {...form.register('email')}
              />
              {requestReset.isError ? (
                <p aria-live="polite" className="auth-error" role="alert">
                  {t(isRateLimited ? 'auth.errorRateLimited' : 'auth.errorGeneric')}
                </p>
              ) : null}
              <SliderAsyncButton pending={requestReset.isPending} size="lg" type="submit">
                {t('auth.sendResetLink')}
              </SliderAsyncButton>
            </form>
          )}
          <Link className="auth-back-link auth-text-link" to="/login">
            <ArrowLeft aria-hidden="true" className="logical-chevron" size={15} />
            {t('auth.backToSignIn')}
          </Link>
        </CardContent>
      </Card>
    </AuthPage>
  );
}
